import { zDateSearchParam } from '@repo/ui/dates'
import { z } from 'zod'
import {
	AUDIT_CATEGORIES,
	type AuditCategory
} from '@/routes/(private)/(shell)/admin/audit/-lib/audit-catalogue'

/**
 * The filters, in the URL, because every one of them is worth sending to
 * somebody: "look at what happened between these two days" is the sentence this
 * screen exists to answer, and it has to survive being pasted into a message.
 *
 * The two dates are calendar days rather than instants — that is what a person
 * picks and what a link should carry — and are widened to the day's edges at
 * the point they become a request. `zDateSearchParam` already catches a
 * malformed one back to `undefined`, so a mangled link opens the unfiltered
 * list instead of an error page.
 *
 * **`period: 'all'` is the only alternative to a date range, not a third
 * value beside it.** The screen's default is the last 30 days without either
 * `from`/`to` or `period` in the URL — see `index.tsx`'s effective-range
 * comment — and `all` is how a link says "no, really, unbounded" instead of
 * being read as the same default. `from`/`to` and `period` are never both
 * set: the filter row drops one whenever it writes the other.
 *
 * `types` carries the five coarse categories `-lib/audit-catalogue.ts` groups
 * the action catalogue into, never a raw action — see `AUDIT_ACTION_CATEGORY`
 * for why the coarser set is what survives a link. A category the screen no
 * longer offers (`devices`, gone with the old sync module in OPSSTD-10523) is
 * dropped rather than refused, so a saved link keeps the filters it still can
 * instead of opening an error page.
 *
 * **THERE IS NO ORGANIZATION FIELD, AND THERE MUST NEVER BE ONE.** The listing
 * is the session's own organization; `list-events` carries no way to name
 * another. A search param that looked like one would be a second, weaker
 * perimeter in the most quotable place there is — a URL.
 */
export const auditSearchSchema = z.object({
	from: zDateSearchParam,
	to: zDateSearchParam,
	period: z.enum(['all']).optional(),
	personId: z.string().optional(),
	types: z
		.array(z.string())
		.optional()
		.transform((types) =>
			types?.filter((type): type is AuditCategory =>
				(AUDIT_CATEGORIES as readonly string[]).includes(type)
			)
		),
	objectType: z.string().optional(),
	objectId: z.string().optional()
})

/**
 * Whether anything is narrowing the list away from its default view.
 *
 * Two screens ask it and must agree: the filter row, which shows a "clear
 * everything" only when there is something to clear, and the empty state, which
 * decides between "these filters matched nothing" and "this organization has no
 * trail yet". Two copies of the predicate would drift the first time a filter
 * was added, and the drift would show up as an empty state telling an
 * administrator the wrong thing.
 *
 * **The default 30-day window does not count as a filter, and `period: 'all'`
 * does.** Nobody narrowed anything by opening the page; asking for the whole
 * history is a deliberate widening away from the default, and the chip and the
 * reset both have to treat it as one.
 *
 * `objectType` counts even though nothing sets it alone: it rides with
 * `objectId` from a row, and a link carrying only the type is still a narrowed
 * list.
 *
 * The retention branch is deliberately not here — whether the period asked for
 * is older than the window is the server's answer, not a comparison this side
 * makes.
 */
export function isAuditFiltered(params: z.infer<typeof auditSearchSchema>) {
	return (
		params.from !== undefined ||
		params.to !== undefined ||
		params.period !== undefined ||
		params.personId !== undefined ||
		params.objectId !== undefined ||
		params.objectType !== undefined ||
		(params.types !== undefined && params.types.length > 0)
	)
}
